import React, { useState } from 'react'
import { Check, WarningCircle } from '@phosphor-icons/react'
import { countDecimalsAndAdd0, getRole } from 'Utils/helperFunctions'
import { ROLE_ADMIN_NAME, ROLE_DEVELOPER_NAME } from 'constant/roles'
import useSessionStore from 'Context/useSessionStore'
import Avatar from 'react-avatar'
import { COLOR_BLACK, COLOR_YELLOW, GET_COLOR_BY_THEME } from 'constant/colors'
import { usePayroll } from 'Hooks'
import { PERMISSION_BY_ROLE } from 'Utils/permissionByRol'
import { Loading } from 'Organisms/Loading'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import ModalConfirmOrCancel from 'Organisms/ModalConfirmOrCancel'

const INIT_MODAL_MESSAGE = {
  open: false,
  message: '',
  success: false
}

const StepUsersTable = ({ users, nextStep, pendingApproval, startDate, endDate, refreshData }) => {
  const { user: userContext } = useSessionStore((state) => ({ user: state.user }))
  const { loadingPayrollHook, handleUpdateApprovedHoursHook } = usePayroll()

  const [selectedUserIds, setSelectedUserIds] = useState([])
  const [confirmApproved, setConfirmApproved] = useState(false)
  const [modalMessage, setModalMessage] = useState(INIT_MODAL_MESSAGE)

  let totalHours = 0
  let totalPrice = 0

  const canApproveSelf = PERMISSION_BY_ROLE.PAYROLL.SELF_ACTIONS.includes(userContext.role)

  const approvableUsers = pendingApproval
    ? users.filter((user) => {
      const hasPendingHours = Object.values(user?.service_details || {}).some((detail) => detail.total_hours > 0)
      const isSelf = user.id === userContext.id

      return hasPendingHours && (!isSelf || canApproveSelf)
    })
    : []
  const approvableUserIds = approvableUsers.map((user) => user.id)

  const handleToggleUser = (userId) => {
    setSelectedUserIds((prev) => (prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]))
  }

  const handleToggleAll = () => {
    setSelectedUserIds(selectedUserIds.length === approvableUserIds.length ? [] : approvableUserIds)
  }

  const handleApproveSelected = async () => {
    let hasError = false

    for (const userId of selectedUserIds) {
      const selectedUser = users.find((user) => user.id === userId)
      if (!selectedUser) continue

      const result = await handleUpdateApprovedHoursHook({
        startDate,
        endDate,
        user_id: userId,
        project_ids: selectedUser.projects.map((item) => item.id)
      })

      if (!result.success) hasError = true
    }

    setSelectedUserIds([])

    setModalMessage({
      open: true,
      message: hasError ? 'Some hours could not be approved' : 'Hours for the selected employees have been approved',
      success: !hasError
    })
  }

  const handleCloseModalMessageAndRefreshData = async () => {
    setModalMessage(INIT_MODAL_MESSAGE)

    if (modalMessage.success) await refreshData()
  }

  return (
    <div className="card p-4">
      {loadingPayrollHook && <Loading />}

      <div className="table-responsive projects-card">
        <table className="table align-middle mb-0 payroll_table">
          <thead>
            <tr>
              {pendingApproval && (
                <th scope="col">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="checkboxSelectAllUsers"
                    disabled={approvableUserIds.length === 0}
                    checked={approvableUserIds.length > 0 && selectedUserIds.length === approvableUserIds.length}
                    onChange={handleToggleAll}
                  />
                </th>
              )}
              <th scope="col">Name</th>
              <th scope="col">Payment Type</th>
              <th scope="col" className="text-end">
                Hours
              </th>
              {[ROLE_ADMIN_NAME, ROLE_DEVELOPER_NAME].includes(userContext.role) && (
                <th scope="col" className="text-end">
                  Payable amount
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              const hoursType = Object.keys(user?.service_details).filter(
                (item) => user?.service_details[item].total_hours > 0
              )
              const canSelectUser = approvableUserIds.includes(user.id)

              return (
                <React.Fragment key={user.id}>
                  {hoursType.map((hourType, index) => {
                    const hours = user?.service_details?.[hourType]?.billable_hours || 0
                    const price =
                      hourType === 'hourly_cost'
                        ? user?.service_details?.[hourType]?.billable_cost || 0
                        : user?.cost_rate?.cost_rate || 0

                    totalHours += hours
                    totalPrice += price

                    return (
                      <tr key={hourType} className="hover">
                        {pendingApproval && index === 0 && (
                          <td rowSpan={hoursType.length}>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              disabled={!canSelectUser}
                              checked={selectedUserIds.includes(user.id)}
                              onChange={() => handleToggleUser(user.id)}
                            />
                          </td>
                        )}
                        <td className="text-no-wrap pointer d-flex align-items-center" onClick={() => nextStep({ userId: user.id })}>
                          <Avatar
                            name={user.full_name}
                            src={user?.logo || null}
                            className="avatarImage me-2"
                            color={GET_COLOR_BY_THEME({ theme: userContext.theme }).AVATAR}
                            fgColor={GET_COLOR_BY_THEME({ theme: userContext.theme }).AVATAR_TEXT}
                            round={true}
                            size="38px"
                            textSizeRatio={3}
                          />

                          <div>
                            {user.full_name}
                            <br />
                            <span className="form-text">{getRole(user.role)}</span>
                          </div>
                        </td>
                        <td className="text-no-wrap">
                          {hourType === 'hourly_cost'
                            ? (
                            <span className="badge text-bg-quaternaly">Hourly rate</span>
                              )
                            : (
                            <span className="badge text-bg-primary">Monthly payment</span>
                              )}
                        </td>
                        <td className="text-end">{countDecimalsAndAdd0({ number: hours })}h</td>
                        {[ROLE_ADMIN_NAME, ROLE_DEVELOPER_NAME].includes(userContext.role) && (
                          <td className="text-end">
                            ${countDecimalsAndAdd0({ number: price })} {hourType === 'hourly_cost' ? '' : '/month'}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </React.Fragment>
              )
            })}

            <br />
            <tr style={{ borderTop: '1px solid rgba(54, 58, 57, 0.25)' }}>
              {pendingApproval && <td style={{ border: 'none' }} />}
              <td style={{ border: 'none' }} className="text-no-wrap text-normal fw-bold">
                Total
              </td>
              <td style={{ border: 'none' }} />
              <td style={{ border: 'none' }} className="text-end text-normal fw-bold">
                {countDecimalsAndAdd0({ number: totalHours })}h
              </td>
              {[ROLE_ADMIN_NAME, ROLE_DEVELOPER_NAME].includes(userContext.role) && (
                <td style={{ border: 'none' }} className="text-end text-normal fw-bold">
                  ${countDecimalsAndAdd0({ number: totalPrice })}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {pendingApproval && (
        <div className="d-grid d-sm-flex justify-content-sm-end mt-4">
          <button
            type="button"
            className="btn btn-primary"
            id="btnApproveSelectedUsers"
            disabled={selectedUserIds.length === 0}
            onClick={() => setConfirmApproved(true)}>
            <Check className="icon" size={15} color={COLOR_BLACK} weight="bold" />
            <span>Approve hours{selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}</span>
          </button>
        </div>
      )}

      {confirmApproved && (
        <ModalConfirmOrCancel
          title={`Do you want to approve hours for ${selectedUserIds.length} selected employee${selectedUserIds.length === 1 ? '' : 's'}?`}
          setVisible={setConfirmApproved}
          btnConfirm="Approve"
          onConfirm={handleApproveSelected}
          icon={<WarningCircle size={56} color={COLOR_YELLOW} />}
        />
      )}

      {modalMessage.open && (
        <ModalSuccess
          visible={true}
          setVisible={handleCloseModalMessageAndRefreshData}
          title={modalMessage.message}
          identifier="modalMessageApprovedUsers"
          error={!modalMessage.success}
        />
      )}
    </div>
  )
}

export default StepUsersTable
